import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight,
  BellRing,
  Building2,
  ChevronRight,
  FlaskConical,
  Layers,
  LineChart,
  Lock,
  PlaySquare,
  Power,
  RefreshCcw,
  Scale,
  ScanSearch,
  ServerOff,
  ShieldCheck,
  Timer,
  WifiOff,
  Zap,
} from 'lucide-react';
import useIsDarkMode from '../hooks/useIsDarkMode';
import InstallPrompt from '../components/InstallPrompt';
import { BrokerStopsDiagram, LookAheadDiagram } from '../components/LandingDiagrams';

// Enquiries mailbox (Google Workspace).
const CONTACT_EMAIL = 'hello@jigritrades.in';

// Every claim below must stay true of the code: if a capability changes,
// change the copy. No return or performance claims anywhere on this page.

const PIPELINE = [
  { step: 'Screen', icon: ScanSearch, desc: '100+ technical and fundamental attributes across 2,000+ NSE stocks.' },
  { step: 'Backtest', icon: PlaySquare, desc: 'Years of daily data with real Zerodha charges, splits and suspensions.' },
  { step: 'Walk-forward', icon: RefreshCcw, desc: 'Rank screener × exit-rule pairs on past data, re-rank on a schedule.' },
  { step: 'Dry run', icon: FlaskConical, desc: 'Run a book on live data without placing orders.' },
  { step: 'Go live', icon: Zap, desc: 'Orders and protective stops placed through your Zerodha account.' },
];

const PILLARS = [
  {
    title: 'Backtests you can believe',
    icon: Lock,
    points: [
      'The engine refuses any read past the simulated date — look-ahead fails loudly instead of slipping in.',
      'Fundamentals are never visible before the day they were published.',
      'The universe includes companies that were later delisted, so results aren’t built on survivors.',
      'Brokerage, STT, stamp duty, exchange and DP charges are modelled exactly.',
    ],
  },
  {
    title: 'Live trading that fails safe',
    icon: ShieldCheck,
    points: [
      'Stop-loss and target orders sit at the broker as GTTs, not in our process.',
      'Every order is recorded before it is sent, so a crash can’t create a duplicate.',
      'Positions are reconciled against the broker every 15 minutes.',
      'Backtest, dry run and live exit at the same levels, by construction.',
    ],
  },
  {
    title: 'One system, idea to order',
    icon: Layers,
    points: [
      'Screeners, backtests, walk-forward ranking, dry runs and live books share one engine.',
      'Exit rules are pinned at entry — a strategy change never re-prices an open position.',
      'Tear sheets: drawdowns, rolling Sharpe, monthly returns, alpha and beta vs Nifty.',
      'Daily data, signals and live phases run on a schedule, with alerts.',
    ],
  },
];

const FAILURES = [
  { icon: ServerOff, when: 'Our server goes down', then: 'Your stops and targets are already at the broker. Open positions stay protected.' },
  { icon: Power, when: 'A process dies mid-order', then: 'The position is rebuilt from broker state. Nothing is booked against an unconfirmed fill.' },
  { icon: WifiOff, when: 'Prices can’t be fetched', then: 'New entries are blocked rather than sized on stale prices.' },
  { icon: Timer, when: 'The daily loss limit trips', then: 'Entries stay halted until a human resets them — no automatic resumption.' },
  { icon: BellRing, when: 'A scheduled job doesn’t finish', then: 'A dead-man’s switch alerts you on Telegram before the next session.' },
  { icon: Scale, when: 'Our records and the broker disagree', then: 'The mismatch is flagged and alerted — never silently “fixed” behind your back.' },
];

// No prices until they are validated; amounts would anchor every later deal.
const PLANS = [
  {
    name: 'Early access',
    tag: 'Free during beta',
    desc: 'The full platform for approved users, in exchange for feedback.',
    points: ['Screener builder and backtests', 'Walk-forward ranking', 'Dry-run and live books via Zerodha'],
    cta: 'Request early access',
    to: '/register',
    featured: true,
  },
  {
    name: 'Pro',
    tag: 'Coming soon',
    desc: 'For traders running several strategies live.',
    points: ['More live books', 'Python screeners', 'Priority data and support'],
    cta: 'Join early access to hear first',
    to: '/register',
  },
  {
    name: 'Enterprise',
    tag: 'Let’s talk',
    desc: 'For brokers, portfolio managers and proprietary desks.',
    points: ['On-premise deployment', 'Annual licence and support', 'Design-partner terms'],
    cta: 'Become a design partner',
    href: `mailto:${CONTACT_EMAIL}?subject=Jigri%20Enterprise`,
  },
];

const FAQ = [
  {
    q: 'What does it cost?',
    a: 'Nothing during early access. Paid plans will be flat subscriptions — we will never take a share of your trading profits.',
  },
  {
    q: 'Which markets and brokers are supported?',
    a: 'NSE cash equities (delivery), with live execution through Zerodha Kite. Other brokers and F&O are on the roadmap, not available today.',
  },
  {
    q: 'Do you give tips or promise returns?',
    a: 'No. Jigri is software for building and running your own rules. It does not recommend securities, and backtested results are not a guarantee of future performance.',
  },
  {
    q: 'Do I need to write code?',
    a: 'No. Screeners can be built from the attribute library. Python is available for custom logic on approved accounts.',
  },
  {
    q: 'How do I get access?',
    a: 'Request access and we’ll review your account. We’re onboarding users in small batches while we harden the platform.',
  },
];

function SectionHeading({ eyebrow, title, desc }) {
  return (
    <div className="max-w-2xl mx-auto text-center flex flex-col gap-3 mb-12">
      <span className="text-sm font-semibold text-primary uppercase tracking-wider">{eyebrow}</span>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{title}</h2>
      {desc && <p className="text-lg text-muted-foreground">{desc}</p>}
    </div>
  );
}

function DiagramRow({ title, desc, children, reverse = false }) {
  return (
    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
      <div className={`flex flex-col gap-3 ${reverse ? 'md:order-2' : ''}`}>
        <h3 className="text-2xl font-bold tracking-tight">{title}</h3>
        <p className="text-muted-foreground leading-relaxed">{desc}</p>
      </div>
      <div className={`rounded-2xl border border-border bg-card p-2 sm:p-6 ${reverse ? 'md:order-1' : ''}`}>
        {children}
      </div>
    </div>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  const isDark = useIsDarkMode();
  const logoSrc = isDark ? '/jigri-logo-dark.png' : '/jigri-logo1.png';
  const iconSrc = isDark ? '/jigri-icon-dark.png' : '/jigri-icon.png';

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col relative overflow-x-hidden">
      <div className="absolute top-0 inset-x-0 h-96 bg-linear-to-b from-primary/5 to-transparent -z-10 pointer-events-none" />

      {/* Nav */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border bg-background/80 backdrop-blur-xs sticky top-0 z-50">
        <img src={logoSrc} alt="Jigri" className="h-8 rounded-sm" />
        <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
          <a href="#platform" className="hover:text-foreground transition-colors">Platform</a>
          <a href="#safety" className="hover:text-foreground transition-colors">Safety</a>
          <a href="#firms" className="hover:text-foreground transition-colors">For firms</a>
          <a href="#plans" className="hover:text-foreground transition-colors">Plans</a>
          <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/login">
            <Button variant="ghost" size="sm">Sign in</Button>
          </Link>
          <Link to="/register">
            <Button size="sm">Request access</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="flex flex-col items-center text-center px-4 py-16 sm:py-24 gap-6">
        <div className="inline-flex items-center rounded-full border border-border bg-muted/50 px-3 py-1 text-sm font-medium">
          Systematic trading for NSE equities
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight max-w-4xl leading-tight">
          From idea to live order — <span className="text-primary">without fooling yourself</span>
        </h1>

        <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl">
          Screen, backtest, walk-forward test and trade your rules in one system — with backtests
          that can’t see the future and live execution built to fail safe.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Link to="/register" className="w-full sm:w-auto">
            <Button size="lg" className="w-full text-base h-12 px-8">
              Request early access <ArrowRight />
            </Button>
          </Link>
          <a href={`mailto:${CONTACT_EMAIL}`} className="w-full sm:w-auto">
            <Button variant="outline" size="lg" className="w-full text-base h-12 px-8">Talk to us</Button>
          </a>
          <div className="w-full sm:w-auto">
            <InstallPrompt />
          </div>
        </div>

        <p className="text-sm text-muted-foreground">NSE cash equities · Live execution via Zerodha Kite</p>

        <div className="relative w-full max-w-5xl mx-auto mt-6 group">
          <div className="absolute -inset-1 bg-linear-to-r from-primary/20 to-secondary/20 rounded-2xl blur-2xl opacity-50 group-hover:opacity-75 transition duration-1000" />
          <div className="relative rounded-2xl border border-border shadow-2xl overflow-hidden bg-background">
            <img src="/hero-image.png" alt="Jigri strategy dashboard" className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      {/* Pipeline */}
      <section id="platform" className="px-4 sm:px-6 py-16 sm:py-24 border-t border-border scroll-mt-20">
        <SectionHeading
          eyebrow="The platform"
          title="One pipeline, research to execution"
          desc="Every stage runs on the same engine, so what you tested is what trades."
        />
        <ol className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PIPELINE.map((p, i) => {
            const Icon = p.icon;
            return (
              <li key={p.step} className="relative rounded-xl border border-border bg-card p-5 flex flex-col gap-3">
                {i < PIPELINE.length - 1 && (
                  <ChevronRight
                    aria-hidden="true"
                    className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-background border border-border p-1 text-primary"
                  />
                )}
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">0{i + 1}</span>
                </div>
                <h3 className="font-semibold text-lg">{p.step}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </li>
            );
          })}
        </ol>
        <div className="max-w-6xl mx-auto mt-4 rounded-xl border border-dashed border-primary/50 bg-primary/5 px-4 py-3 text-center text-sm">
          <span className="font-semibold text-primary">One engine underneath</span>
          <span className="text-muted-foreground"> — the same signals, exit levels and charges from backtest to live.</span>
        </div>
      </section>

      {/* Pillars */}
      <section id="why" className="px-4 sm:px-6 py-16 sm:py-24 bg-muted/30 border-y border-border scroll-mt-20">
        <SectionHeading
          eyebrow="Why Jigri"
          title="Built for the parts that usually go wrong"
        />
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <Card key={p.title} className="h-full">
                <CardContent className="p-6 flex flex-col gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{p.title}</h3>
                  <ul className="flex flex-col gap-3">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="mt-16">
          <DiagramRow
            title="The backtest never sees tomorrow"
            desc="Each simulated day can only read data that existed on that day. Prices after it, and results published after it, are blocked by the engine itself — a read past the date raises an error instead of quietly inflating returns."
          >
            <LookAheadDiagram className="w-full h-auto" />
          </DiagramRow>
        </div>
      </section>

      {/* Failure modes */}
      <section id="safety" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
        <SectionHeading
          eyebrow="Live safety"
          title="What happens when something breaks"
          desc="Real money is on the line, so every failure has a defined, conservative outcome."
        />
        <div className="mb-12">
          <DiagramRow
            reverse
            title="Your stops live at the broker"
            desc="When a position opens, its stop-loss and target are placed with Zerodha as GTT orders. They trigger on the exchange at your levels whether or not Jigri is running."
          >
            <BrokerStopsDiagram className="w-full h-auto" />
          </DiagramRow>
        </div>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          {FAILURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.when} className="flex gap-4 rounded-xl border border-border bg-card p-5">
                <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold">{f.when}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.then}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* For firms */}
      <section id="firms" className="px-4 sm:px-6 py-16 sm:py-24 bg-muted/30 border-y border-border scroll-mt-20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="flex flex-col gap-4">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">For firms</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Run it on your own infrastructure</h2>
            <p className="text-lg text-muted-foreground">
              For brokers, portfolio managers and proprietary desks that need research, risk controls
              and execution inside their own environment. We’re working with a small number of design
              partners on on-premise deployment and enterprise licensing.
            </p>
            <div>
              <a href={`mailto:${CONTACT_EMAIL}?subject=Jigri%20for%20firms`}>
                <Button size="lg" className="h-12 px-8">
                  Become a design partner <ArrowRight />
                </Button>
              </a>
            </div>
          </div>
          <Card>
            <CardContent className="p-6 flex flex-col gap-4">
              {[
                { icon: Building2, t: 'Your servers, your data', d: 'Deploy inside your own network; credentials never leave it.' },
                { icon: LineChart, t: 'Shared research engine', d: 'The same bias-safe backtesting and walk-forward ranking.' },
                { icon: ShieldCheck, t: 'Controls you can audit', d: 'Kill switches, reconciliation and alerting built in.' },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t} className="flex gap-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t}</h3>
                    <p className="text-sm text-muted-foreground">{d}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
        <SectionHeading
          eyebrow="Plans"
          title="Simple, flat subscriptions"
          desc="We never take a share of your trading profits."
        />
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const button = (
              <Button
                variant={plan.featured ? 'default' : 'outline'}
                className="w-full h-11 whitespace-normal"
              >
                {plan.cta}
              </Button>
            );
            return (
              <Card
                key={plan.name}
                className={plan.featured ? 'h-full border-primary shadow-md' : 'h-full'}
              >
                <CardContent className="p-6 flex flex-col gap-4 h-full">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-xl font-semibold">{plan.name}</h3>
                    <span className="text-2xl font-bold text-primary">{plan.tag}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.desc}</p>
                  <ul className="flex flex-col gap-2 flex-1">
                    {plan.points.map((pt) => (
                      <li key={pt} className="flex gap-2 text-sm">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.to ? <Link to={plan.to}>{button}</Link> : <a href={plan.href}>{button}</a>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
        <SectionHeading eyebrow="FAQ" title="Questions, answered plainly" />
        <div className="max-w-3xl mx-auto flex flex-col divide-y divide-border border-y border-border">
          {FAQ.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
                {f.q}
                <span className="text-muted-foreground transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto rounded-2xl border border-border bg-card p-8 sm:p-12 text-center flex flex-col items-center gap-5">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Test honestly. Trade carefully.</h2>
          <p className="text-lg text-muted-foreground max-w-xl">
            Request access to start building, or talk to us about running Jigri for your firm.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Link to="/register" className="w-full sm:w-auto">
              <Button size="lg" className="w-full h-12 px-8">Request early access</Button>
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full h-12 px-8">Talk to us</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-border px-4 sm:px-6 py-8 flex flex-col gap-6 text-sm text-muted-foreground">
        <p className="max-w-4xl leading-relaxed">
          Jigri provides software tools for building and running your own trading rules. It does not
          provide investment advice or recommend any security. Trading in securities involves risk of
          loss, and backtested or past performance does not guarantee future results.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={iconSrc} alt="Jigri" className="h-4 opacity-60 grayscale" />
            <span>© {new Date().getFullYear()} Jigri. All rights reserved.</span>
          </div>
          <div className="flex gap-6">
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-foreground transition-colors">Contact</a>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
